import { useEffect, useState } from 'react';

/**
 * `true` a partir do breakpoint `lg` do Tailwind (1024px) — o ponto em que o
 * app troca a navegação inferior pelo menu lateral.
 *
 * Por que NÃO reusar `useIsMobile` (`src/hooks/use-mobile.tsx`): ele mede
 * `window.innerWidth`, que no jsdom vale 1024 por padrão. Toda a suíte passaria
 * a renderizar a árvore de DESKTOP, trocando a barra inferior pelo menu lateral
 * em ~39 páginas de teste que nada têm a ver com layout. Aqui a leitura é do
 * `matchMedia`, que o `src/test/setup.ts` mocka com `matches: false` — em teste
 * a árvore continua sendo a mobile, que é a que o app entrega no aparelho.
 *
 * O estado nasce já resolvido (lazy initializer) em vez de dentro do efeito:
 * no navegador o primeiro quadro já sai na largura certa, sem o piscar da barra
 * inferior aparecendo e sumindo.
 */
const DESKTOP_QUERY = '(min-width: 1024px)';

function matches(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DESKTOP_QUERY).matches;
}

export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(matches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mql.matches);
    // Sincroniza uma vez: entre o primeiro render e este efeito a janela pode
    // ter mudado (girar o tablet, abrir o devtools).
    onChange();
    mql.addEventListener('change', onChange);
    // `resize` é REDE, não redundância decorativa. Quando o `change` do
    // MediaQueryList não chega, o app fica num estado impossível — menu lateral
    // montado numa janela estreita, com o conteúdo espremido ao lado dele. Foi
    // exatamente o que se mediu ao redimensionar pelo painel de preview, onde
    // NENHUM dos dois eventos é emitido. Custa um `setState` com o mesmo valor
    // (no-op no React) e cobre o caso em que só um dos dois chega.
    window.addEventListener('resize', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
      window.removeEventListener('resize', onChange);
    };
  }, []);

  return isDesktop;
}
